import { api, apiUrl, ApiClientError } from './api';

interface UploadInit {
  id: string;
  provider: 'r2' | 'nas';
  uploadUrl?: string;
  partBytes?: number;
  totalParts?: number;
  completeUrl?: string;
  resumed?: boolean;
}

function contentType(file: File): string {
  if (file.type) return file.type;
  const extension = file.name.split('.').at(-1)?.toLowerCase();
  const types: Record<string, string> = {
    pdf: 'application/pdf', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp',
    txt: 'text/plain', csv: 'text/csv', md: 'text/markdown',
  };
  return types[extension ?? ''] ?? 'application/octet-stream';
}

async function hashFile(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function uploadPart(path: string, body: Blob): Promise<Response> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 30_000);
  try {
    return await fetch(apiUrl(path), { method: 'PUT', credentials: 'include', body, signal: controller.signal });
  } finally { window.clearTimeout(timeout); }
}

function duplicateFileId(error: unknown): string | null {
  if (!(error instanceof ApiClientError) || error.code !== 'FILE_DUPLICATE') return null;
  const details = error.details as { existingFile?: { id?: string } } | undefined;
  return details?.existingFile?.id ?? null;
}

export async function uploadLibraryFile(
  file: File,
  onProgress: (percent: number, message: string) => void,
): Promise<{ storedFileId: string; duplicate: boolean }> {
  if (file.size > 200 * 1024 * 1024) throw new Error('ไฟล์เอกสารต้องมีขนาดไม่เกิน 200 MB');
  onProgress(2, 'กำลังตรวจ checksum…');
  const sha256 = await hashFile(file);
  onProgress(15, 'กำลังเตรียมพื้นที่จัดเก็บ…');
  let initiated: { data: UploadInit };
  try {
    initiated = await api('/api/files/uploads', { method: 'POST', body: JSON.stringify({
      fileName: file.name, contentType: contentType(file), sizeBytes: file.size, sha256,
      purpose: 'document_original',
    }) });
  } catch (error) {
    const existing = duplicateFileId(error);
    if (existing) return { storedFileId: existing, duplicate: true };
    throw error;
  }
  const id = initiated.data.id;
  if (initiated.data.provider === 'r2' && initiated.data.uploadUrl) {
    const response = await fetch(apiUrl(initiated.data.uploadUrl), { method: 'PUT', credentials: 'include', headers: { 'Content-Type': contentType(file) }, body: file });
    if (!response.ok) throw new Error('อัปโหลดไฟล์ไม่สำเร็จ');
  } else {
    const partBytes = initiated.data.partBytes; const totalParts = initiated.data.totalParts;
    if (!partBytes || !totalParts || !initiated.data.completeUrl) throw new Error('ข้อมูลอัปโหลด NAS ไม่ครบถ้วน');
    for (let part = 1; part <= totalParts; part += 1) {
      onProgress(15 + Math.round((part - 1) / totalParts * 75), `กำลังส่งไฟล์ส่วน ${part}/${totalParts}…`);
      const response = await uploadPart(`/api/files/${id}/parts/${part}`, file.slice((part - 1) * partBytes, Math.min(part * partBytes, file.size)));
      if (!response.ok) throw new Error(`NAS รับไฟล์ส่วน ${part} ไม่สำเร็จ`);
    }
    try { await api(initiated.data.completeUrl, { method: 'POST' }); }
    catch (error) { const existing = duplicateFileId(error); if (existing) return { storedFileId: existing, duplicate: true }; throw error; }
  }
  onProgress(95, 'บันทึกไฟล์ต้นฉบับแล้ว…');
  return { storedFileId: id, duplicate: false };
}
